import type { AlarmItem, AlarmSeverity } from '@zyxantech/wiz'
import type { AlarmEvent } from '@/api/alarms.api'

/** Map the API severity (info|warning|critical) to the wiz AlarmSeverity set. */
export function toWizSeverity(severity: string): AlarmSeverity {
  switch (severity) {
    case 'critical':
      return 'critical'
    case 'warning':
      return 'high'
    case 'info':
      return 'info'
    default:
      return 'medium'
  }
}

/** Sort weight (higher = more urgent) for the wiz severity set. */
const WEIGHT: Record<AlarmSeverity, number> = {
  critical: 5,
  high: 4,
  medium: 3,
  low: 2,
  info: 1,
}

/** Normalise the API status string to the wiz AlarmItem status set. */
function toItemStatus(status: string | null): AlarmItem['status'] {
  return status === 'acknowledged' || status === 'resolved' ? status : 'active'
}

/** Map an API alarm event to a wiz AlarmItem, resolving a human source label. */
export function toAlarmItem(event: AlarmEvent, source: string): AlarmItem {
  return {
    id: event.id,
    severity: toWizSeverity(event.severity),
    message: event.message ?? '',
    source,
    timestamp: new Date(event.triggeredAt),
    status: toItemStatus(event.status),
    ...(event.triggeredValue !== null ? { value: event.triggeredValue } : {}),
  }
}

/** Comparator: most severe first, then most recent first. */
export function compareAlarmItems(a: AlarmItem, b: AlarmItem): number {
  const bySeverity = WEIGHT[b.severity] - WEIGHT[a.severity]
  if (bySeverity !== 0) return bySeverity
  return b.timestamp.getTime() - a.timestamp.getTime()
}
